import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClearData = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedDatabases, setSelectedDatabases] = useState({
    members: false,
    advertisers: false,
    bills: false,
    dispatches: false
  });
  const [showConfirm, setShowConfirm] = useState(false);

  const databases = [
    { key: 'members', label: 'Members Database', color: 'blue' },
    { key: 'advertisers', label: 'Advertisers Database', color: 'green' },
    { key: 'bills', label: 'Bills Database', color: 'purple' },
    { key: 'dispatches', label: 'Dispatches Database', color: 'orange' }
  ];

  const handleCheckboxChange = (key) => {
    setSelectedDatabases(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const hasSelection = Object.values(selectedDatabases).some(val => val);

  const handleClearData = async () => {
    if (!hasSelection) {
      toast.error('Please select at least one database to clear');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/clear-data`, selectedDatabases, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      toast.success('Selected databases cleared successfully!');
      
      // Reset selections
      setSelectedDatabases({
        members: false,
        advertisers: false,
        bills: false,
        dispatches: false
      });
      setShowConfirm(false);

      // Show deleted counts
      const counts = response.data.deleted_counts;
      const summary = Object.entries(counts)
        .filter(([key]) => selectedDatabases[key])
        .map(([key, count]) => `${key}: ${count} records`)
        .join(', ');
      
      if (summary) {
        toast.info(`Deleted: ${summary}`);
      }
    } catch (error) {
      console.error('Clear data error:', error);
      toast.error(error.response?.data?.detail || 'Failed to clear data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 py-8">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center space-x-4 mb-8">
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2" size={18} />
            Back
          </Button>
          <h1 className="text-4xl font-bold text-red-600">
            Clear Database
          </h1>
        </div>

        {/* Warning Banner */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
          <div className="flex">
            <AlertTriangle className="h-6 w-6 text-yellow-400 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Warning: Destructive Action</h3>
              <p className="mt-1 text-sm text-yellow-700">
                This will permanently delete all records from the selected databases. This action cannot be undone.
              </p>
            </div>
          </div>
        </div>

        {/* Database Selection */}
        <div className="form-section mb-8">
          <h2 className="text-xl font-semibold mb-4 text-red-600">Select Databases to Clear</h2>
          <div className="space-y-4">
            {databases.map(({ key, label, color }) => (
              <div key={key} className="flex items-center p-4 border-2 border-gray-200 rounded-lg hover:border-red-300 transition-colors">
                <input
                  type="checkbox"
                  id={key}
                  checked={selectedDatabases[key]}
                  onChange={() => handleCheckboxChange(key)}
                  className="w-5 h-5 text-red-600 rounded focus:ring-red-500"
                />
                <Label htmlFor={key} className="ml-3 text-lg font-medium cursor-pointer">
                  {label}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        {!showConfirm ? (
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={!hasSelection}
            className="w-full"
            style={{ backgroundColor: '#dc2626', color: 'white' }}
          >
            <Trash2 className="mr-2" size={18} />
            Clear Selected Databases
          </Button>
        ) : (
          <div className="space-y-4">
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
              <h3 className="text-lg font-bold text-red-800 mb-2">Confirm Deletion</h3>
              <p className="text-red-700 mb-4">
                Are you absolutely sure? This will permanently delete:
              </p>
              <ul className="list-disc list-inside text-red-700 mb-4">
                {Object.entries(selectedDatabases)
                  .filter(([_, selected]) => selected)
                  .map(([key]) => (
                    <li key={key} className="font-semibold">
                      {databases.find(db => db.key === key)?.label}
                    </li>
                  ))}
              </ul>
              <p className="text-sm text-red-600 font-medium">
                User accounts will be preserved.
              </p>
            </div>
            
            <div className="flex space-x-4">
              <Button
                onClick={() => setShowConfirm(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleClearData}
                disabled={loading}
                className="flex-1"
                style={{ backgroundColor: '#dc2626', color: 'white' }}
              >
                <Trash2 className="mr-2" size={18} />
                {loading ? 'Clearing...' : 'Yes, Clear Data'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClearData;
